import axios from "axios";
import { getAppContext } from '../config/appContext';
import { environment } from './environment';
import { message } from 'antd';
import tokenStorage from '../utils/tokenStorage';

const ctx = getAppContext();
const fallbackTenantSlug = process.env.REACT_APP_TENANT_SLUG;

const api = axios.create({
  baseURL: ctx.apiBaseUrl,
  timeout: environment.API_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
  withCredentials: true,
});

let isRedirecting = false;
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const clearSession = () => {
  tokenStorage.clear();
};

const handleTokenExpiration = () => {
  if (isRedirecting) return;
  isRedirecting = true;
  clearSession();
  message.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
  window.location.href = '/login';
};

api.interceptors.request.use(
  (config) => {
    if (fallbackTenantSlug && ctx.mode === 'tenant' && !ctx.tenantSlug) {
      config.headers['X-Tenant'] = fallbackTenantSlug;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    const token = tokenStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/renovar-token')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post('/auth/renovar-token');
        const { token, expiresIn } = response.data;

        if (!token) {
          throw new Error('No access token in refresh response');
        }

        tokenStorage.setAccessToken(token, expiresIn);
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        originalRequest.headers.Authorization = `Bearer ${token}`;

        processQueue(null, token);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        handleTokenExpiration();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
